import { useQuery } from '@tanstack/react-query'
import { type RefObject, useMemo } from 'react'
import { type Control, Controller, type FieldErrors } from 'react-hook-form'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import type { ComboboxOption } from '@/features/marquee/components/combobox-field'
import {
	RequiredLabel,
	useErrorText
} from '@/features/marquee/components/event-form-atoms'
import { ManagerMultiSelect } from '@/features/marquee/components/manager-multi-select'
import type { EventFormValues } from '@/features/marquee/form-schemas'
import { ctasQueryOptions } from '@/features/marquee/query-options'
import { useTranslation } from '@/translations/i18n'

interface EventFormCtasSectionProps {
	control: Control<EventFormValues>
	errors: FieldErrors<EventFormValues>
	/**
	 * Whether the event must carry at least one code. True for a presentation,
	 * whose check-ins snapshot this set; false for a tune-up, which captures per
	 * attendee as well and may leave the blanket selection empty.
	 */
	required: boolean
	/** Where to portal the option list. */
	popupContainer?: RefObject<HTMLElement | null>
}

/**
 * Blanket-CTA picker, shown in both capture modes; the first selected CTA is
 * the lead.
 */
export function EventFormCtasSection({
	control,
	errors,
	required,
	popupContainer
}: EventFormCtasSectionProps) {
	const { t } = useTranslation()
	const { data: ctas = [], isLoading } = useQuery(ctasQueryOptions())
	const ctaOptions = useMemo<ComboboxOption[]>(
		() => ctas.map((c) => ({ value: c.code, label: c.label })),
		[ctas]
	)
	const errorText = useErrorText()

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-col gap-1'>
				<h2 className='font-medium text-base text-neutral-900'>
					{t('marquee:ctas_section_title')}
				</h2>
				<p className='text-neutral-500 text-sm'>
					{t('marquee:ctas_section_description')}
				</p>
			</div>
			<Controller
				control={control}
				name='ctas'
				render={({ field }) => (
					<div className='flex flex-col gap-2'>
						<Label htmlFor='ctas'>
							{required ? (
								<RequiredLabel
									label={t('marquee:form_ctas_label')}
								/>
							) : (
								t('marquee:form_ctas_label_optional')
							)}
						</Label>
						<ManagerMultiSelect
							emptyLabel={t('marquee:combobox_empty')}
							hasError={!!errors.ctas}
							id='ctas'
							loading={isLoading}
							onChange={field.onChange}
							options={ctaOptions}
							placeholder={t('marquee:cta_placeholder')}
							popupContainer={popupContainer}
							ref={field.ref}
							removeLabel={(name) =>
								t('marquee:ctas_remove', { name })
							}
							searchPlaceholder={t(
								'marquee:combobox_search_placeholder'
							)}
							value={field.value ?? []}
						/>
						<InputHelpText
							error={errorText(errors.ctas?.message)}
						/>
					</div>
				)}
			/>
		</div>
	)
}
